import type { BaseLevelData, BaseLevelGameData } from "@wolfgames/game-kit";
import { GameSaveService } from "@wolfgames/game-kit";

export interface ClueHunterLevel extends BaseLevelData {
  scores: number[];
  streaks: number[];
  completionTime: number;
}

export interface ClueHunterGameData extends BaseLevelGameData<ClueHunterLevel> {
  highestSeasonCompletedId: string | null;
}

class ClueHunterSaveService {
  private static instance: ClueHunterSaveService;
  private saveService: GameSaveService<ClueHunterGameData>;

  public constructor() {
    this.saveService = new GameSaveService<ClueHunterGameData>({
      appId: "clue-hunter",
      currentVersion: 1,
      initialData: {
        gameId: "clue-hunter",
        lastPlayed: Date.now(),
        levels: [],
        highestSeasonCompletedId: null,
      },
      migrations: {
        // Migrates from v1 -> v2
        // 1: (oldEnvelope: any) => {
        //   // Example migration version hook
        //   return {
        //     ...oldEnvelope,
        //     gameData: {
        //       ...oldEnvelope.gameData,
        //       highestSeasonCompletedId: null,
        //       levels: oldEnvelope.gameData.levels.map((l: any) => ({
        //         ...l,
        //         streak: l.streak || 0
        //       }))
        //     }
        //   };
        // }
      },
      onQuotaExceeded: (err) => {
        // Handle UI notification for storage limits specifically for this game
        console.warn("Clue Hunter storage limit reached.", err.message);
      },
    });
  }

  public static getInstance(): ClueHunterSaveService {
    if (!ClueHunterSaveService.instance) {
      ClueHunterSaveService.instance = new ClueHunterSaveService();
    }
    return ClueHunterSaveService.instance;
  }

  public init() {
    const envelope = this.saveService.load();

    if (!envelope) {
      console.log("No saved game data found, starting fresh");
      return;
    }

    const { identity, data } = envelope;
    if (identity) {
      console.log(`Welcome back, ${identity.gamerTag}`);
    }

    if (data?.levels) {
      console.log("User has existing data.");
      // const completedLevelIds = data.levels
      //   .filter((l) => l.isCompleted)
      //   .map((l) => l.levelId);
    }
  }

  public getSave() {
    return this.saveService.load();
  }

  public completeLevel(
    levelId: string,
    score: number,
    timeSpent: number,
  ): void {
    this.saveService.update((current) => {
      // Guard against null data (can happen on mobile)
      if (!current || !current.levels) {
        return current;
      }

      const level = current.levels.find((l) => l.levelId === levelId);

      if (level) {
        // Update existing progress
        level.scores.push(score);
        level.isCompleted = true;
        level.completionTime = timeSpent;
      } else {
        // Create new level entry
        current.levels.push({
          levelId,
          isCompleted: true,
          scores: [score],
          streaks: [],
          completionTime: timeSpent,
        });
      }

      return current;
    });
  }

  public resetProgress(): void {
    this.saveService.clear();
  }

  /**
   * Check if a specific level/episode has been completed
   */
  public isLevelCompleted(levelId: string): boolean {
    const save = this.getSave();
    if (!save?.data?.levels) return false;
    return save.data.levels.some((l) => l.levelId === levelId && l.isCompleted);
  }

  /**
   * Get all completed level IDs
   */
  public getCompletedLevelIds(): string[] {
    const save = this.getSave();
    if (!save?.data?.levels) return [];
    return save.data.levels.filter((l) => l.isCompleted).map((l) => l.levelId);
  }
}

export const saveService = ClueHunterSaveService.getInstance();

export default ClueHunterSaveService;
